import axios from "axios";

// Get base URL from environment variables with fallback
const baseURL =
  import.meta.env.VITE_REACT_APP_API_URL || "http://localhost:3000";

const instance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Request interceptor to add token to headers
// Using a function to get store to avoid circular dependency
let getStore: (() => any) | null = null;

export const setStoreGetter = (storeGetter: () => any) => {
  getStore = storeGetter;
};

instance.interceptors.request.use(
  (config) => {
    if (getStore) {
      try {
        const store = getStore();
        const state = store.getState();
        const token = state.loginSignup?.token;

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Silently fail if store is not available
        console.warn("Could not get token from store:", error);
      }
    } else {
      console.warn("⚠️ Store getter not initialized");
    }

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging
instance.interceptors.response.use(
  (response) => {
    console.log("✅ Axios Response:", {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  (error) => {
    console.error("❌ Axios Response Error:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      data: error.response?.data,
    });

    if (error.response?.status === 401) {
      const requestUrl: string = error.config?.url || "";
      const isAuthRequest = requestUrl.startsWith("/auth/");
      const isOnLoginPage = window.location.pathname === "/login";

      if (!isAuthRequest && !isOnLoginPage) {
        import("@/utils/logout")
          .then(({ logout }) => logout())
          .finally(() => {
            try {
              localStorage.clear();
              sessionStorage.clear();
            } catch (e) {
              console.warn("Could not clear local cache:", e);
            }
            // ?reauth=1 triggers a second clear in index.html before the app
            // boots, so redux-persist and razorpay can't resurrect stale keys.
            window.location.href = "/login?reauth=1";
          });
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
